import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraDataTable, SpecraEmptyState, SpecraInlineBanner, SpecraKeyValueRow, SpecraLink, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { format } from 'date-fns';
import { GitBranch } from 'lucide-react';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { usePursuitRecord } from '../PursuitRecordContext';
import { PursuitSectionFrame } from '../PursuitSectionFrame';
import { usePursuitSurgery } from '../surgery/surgeryHooks';
const OP_LABEL = {
    split: 'Split',
    merge: 'Merge',
    move: 'Move',
    link: 'Move',
    detach: 'Detach'
};
function PursuitLink({ value }) {
    if (!value?.label) return /*#__PURE__*/ _jsx(_Fragment, {
        children: "—"
    });
    if (!value.href) return /*#__PURE__*/ _jsx(_Fragment, {
        children: value.label
    });
    return /*#__PURE__*/ _jsx(SpecraLink, {
        href: value.href,
        renderLink: renderRouterLink,
        children: value.label
    });
}
function joinRefs(refs) {
    if (refs.length === 0) return '—';
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: refs.map((r, i)=>/*#__PURE__*/ _jsxs("span", {
                children: [
                    i > 0 ? ', ' : '',
                    /*#__PURE__*/ _jsx(PursuitLink, {
                        value: r
                    })
                ]
            }, r.id))
    });
}
const COLUMNS = [
    {
        id: 'date',
        header: 'Date',
        cell: (e)=>e.createdAt ? format(new Date(e.createdAt), 'd MMM yyyy') : '—'
    },
    {
        id: 'op',
        header: 'Operation',
        cell: (e)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "neutral",
                children: OP_LABEL[e.op] ?? e.op
            })
    },
    {
        id: 'from',
        header: 'From',
        cell: (e)=>/*#__PURE__*/ _jsx(PursuitLink, {
                value: e.from
            })
    },
    {
        id: 'to',
        header: 'To',
        cell: (e)=>/*#__PURE__*/ _jsx(PursuitLink, {
                value: e.to
            })
    },
    {
        id: 'items',
        header: 'Items',
        cell: (e)=>e.moved.length === 0 ? '0' : /*#__PURE__*/ _jsx(SpecraTooltip, {
                content: e.moved.map((m)=>m.label || m.kind).join(', '),
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    as: "span",
                    size: "body-ui",
                    tabIndex: 0,
                    children: e.moved.length
                })
            })
    },
    {
        id: 'reason',
        header: 'Reason',
        cell: (e)=>e.reason || '—'
    },
    {
        id: 'by',
        header: 'By',
        cell: (e)=>e.actorDisplayName || '—'
    }
];
/** Splits & merges (GRW-19): the pursuit's surgery history and its sibling / merge edges. */ export function SurgerySection() {
    const { captureProjectId, header } = usePursuitRecord();
    const events = usePursuitSurgery(captureProjectId);
    const related = header?.related;
    const siblings = related?.siblings ?? [];
    // GetPursuitHeader lists the split-from pursuit first among siblings
    // (context line "split from …"), then the split children.
    const splitFrom = header?.contextLine.includes('split from') ? siblings.slice(0, 1) : [];
    const others = splitFrom.length ? siblings.slice(1) : siblings;
    const rows = events.data?.events ?? [];
    return /*#__PURE__*/ _jsxs(PursuitSectionFrame, {
        title: "Splits & merges",
        children: [
            (siblings.length > 0 || related?.mergedInto) && /*#__PURE__*/ _jsxs("div", {
                children: [
                    splitFrom.length > 0 && /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Split from",
                        value: joinRefs(splitFrom)
                    }),
                    others.length > 0 && /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Siblings",
                        value: joinRefs(others)
                    }),
                    related?.mergedInto && /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Merged into",
                        value: /*#__PURE__*/ _jsx(PursuitLink, {
                            value: related.mergedInto
                        })
                    })
                ]
            }),
            events.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                variant: "framed",
                children: "The surgery history could not be loaded."
            }) : /*#__PURE__*/ _jsx(SpecraDataTable, {
                columns: COLUMNS,
                rows: rows,
                loading: events.isLoading,
                getRowId: (e)=>e.id,
                testId: Testids.pursuitSurgery.eventsTable,
                empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    description: "When this pursuit is split into parts or merged with another, the history shows here.",
                    framed: true,
                    icon: /*#__PURE__*/ _jsx(GitBranch, {
                        className: "h-5 w-5"
                    }),
                    title: "No splits or merges yet"
                })
            })
        ]
    });
}
